/**
 * Purchase options: a product card's Buy Box and its phone quick view (snippets/component-purchase-options.liquid).
 *
 * Buy Box: the card's Add to Cart opens the dialog instead of adding. From 641px it opens inside the card over its
 * lower half, as live's PT242 panel does: a non-modal dialog that Escape, the close button or a click outside it
 * shuts, and that shuts when focus moves on. Below 641px it is a modal sheet from the bottom of the screen.
 *
 * Quick view: below 768px a tap on the card's image or title opens the same dialog as a modal, with the gallery
 * (Swiper, loaded by the theme), the rating, the benefit tags and a link to the product page: live's PT396.
 *
 * Both add through the dialog's own product form, which assets/cart.js submits; the Subscribe & Save switch is
 * component-selling-plan-cards. The card's form keeps its first-plan add for when this script never runs, so its
 * submit is cancelled here and cart.js leaves a cancelled submit alone. The dialog shuts once the cart has the
 * line, and the cart drawer takes over.
 */

const PANEL_QUERY = 'screen and (min-width: 641px)';
const WIDE_QUERY = 'screen and (min-width: 768px)';
const MODES = ['panel', 'sheet', 'quick'];

if (!customElements.get('purchase-options')) {
  customElements.define(
    'purchase-options',
    class PurchaseOptions extends HTMLElement {
      panelQuery = window.matchMedia(PANEL_QUERY);
      wideQuery = window.matchMedia(WIDE_QUERY);
      opener = null;
      swiper = null;

      get dialog() {
        return this.querySelector('.purchase-options__dialog');
      }

      connectedCallback() {
        this.card = this.closest('.product-card');
        this.cardForm = this.card?.querySelector('.product-card__form');

        if (this.hasAttribute('data-buy-box')) this.cardForm?.addEventListener('submit', this.onCardSubmit);
        if (this.hasAttribute('data-quick-view')) this.card?.addEventListener('click', this.onCardClick);
        this.addEventListener('click', this.onClick);
        this.dialog?.addEventListener('close', this.onClose);
        document.addEventListener('cart:change', this.onCartChange);
        this.panelQuery.addEventListener('change', this.onBreakpoint);
        this.wideQuery.addEventListener('change', this.onBreakpoint);
      }

      disconnectedCallback() {
        this.cardForm?.removeEventListener('submit', this.onCardSubmit);
        this.card?.removeEventListener('click', this.onCardClick);
        this.removeEventListener('click', this.onClick);
        this.dialog?.removeEventListener('close', this.onClose);
        document.removeEventListener('cart:change', this.onCartChange);
        this.panelQuery.removeEventListener('change', this.onBreakpoint);
        this.wideQuery.removeEventListener('change', this.onBreakpoint);
        this.stopLightDismiss();
        this.swiper?.destroy();
        this.swiper = null;
      }

      /**
       * The card's Add to Cart. Cancelling the submit is what tells assets/cart.js not to add.
       * @param {SubmitEvent} event
       */
      onCardSubmit = (event) => {
        event.preventDefault();
        this.open(this.panelQuery.matches ? 'panel' : 'sheet', event.submitter);
      };

      /**
       * A tap on the card's image or title, below 768px.
       * @param {MouseEvent} event
       */
      onCardClick = (event) => {
        if (this.wideQuery.matches || event.defaultPrevented) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const link = event.target.closest('.product-card__media-link, .product-card__title');
        if (!link || this.dialog?.contains(link)) return;

        event.preventDefault();
        // The image link is out of the tab order, so focus comes back to the title either way
        this.open('quick', this.card.querySelector('.product-card__title'));
      };

      /**
       * @param {MouseEvent} event
       */
      onClick = (event) => {
        if (event.target.closest('[data-purchase-options-close]')) {
          this.dialog?.close();
          return;
        }

        // The panel fills the dialog, so a click on the dialog itself landed on a modal's backdrop
        if (event.target === this.dialog) this.dialog.close();
      };

      /**
       * Shuts once the cart has the line this dialog's form sent; the cart drawer opens on the same event.
       * @param {CustomEvent} event
       */
      onCartChange = (event) => {
        if (!this.dialog?.open || !this.contains(event.detail?.source)) return;
        this.dialog.close();
      };

      // Each mode is chosen for the width it opened at
      onBreakpoint = () => {
        if (this.dialog?.open) this.dialog.close();
      };

      /**
       * @param {'panel' | 'sheet' | 'quick'} mode
       * @param {HTMLElement | null} [opener] - Where focus goes back to on close.
       */
      open(mode, opener) {
        const dialog = this.dialog;
        if (!dialog) return;
        // Switching mode: the queued `close` will find the dialog open again and skip its clean-up, so do it here
        if (dialog.open) dialog.close();
        this.stopLightDismiss();

        this.opener = opener || document.activeElement;
        for (const name of MODES) dialog.classList.toggle(`purchase-options__dialog--${name}`, name === mode);
        // assets/critical.css locks the page behind an open dialog with this attribute; the panel leaves it scrolling
        dialog.toggleAttribute('scroll-lock', mode !== 'panel');

        if (mode === 'panel') {
          dialog.show();
          this.startLightDismiss();
        } else {
          dialog.showModal();
        }

        if (mode === 'quick') this.initGallery();
        // Focus goes to the dialog itself, not its first control: showModal() would land on the close button and
        // draw its focus ring around the cross after a tap (Collections QA rows 16 and 17)
        dialog.focus({ preventScroll: true });
      }

      /* The single place every dismissal arrives at. */
      onClose = () => {
        const dialog = this.dialog;
        // `close` is queued; the dialog may have been reopened in another mode before it fired
        if (dialog.open) return;

        this.stopLightDismiss();
        const focused = document.activeElement;
        // Not when focus has moved on: to the cart drawer after an add, or wherever the shopper tabbed or clicked
        if (this.opener?.isConnected && (focused === document.body || dialog.contains(focused))) {
          this.opener.focus({ preventScroll: true });
        }
        this.opener = null;
      };

      /* The non-modal panel has no backdrop and no built-in Escape, so the document provides both. */
      startLightDismiss() {
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
        document.addEventListener('focusin', this.onDocumentFocusIn);
      }

      stopLightDismiss() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
        document.removeEventListener('focusin', this.onDocumentFocusIn);
      }

      /**
       * @param {MouseEvent} event
       */
      onDocumentClick = (event) => {
        if (!this.dialog.contains(event.target)) this.dialog.close();
      };

      /**
       * @param {KeyboardEvent} event
       */
      onDocumentKeydown = (event) => {
        if (event.key === 'Escape') this.dialog.close();
      };

      /**
       * @param {FocusEvent} event
       */
      onDocumentFocusIn = (event) => {
        if (!this.dialog.contains(event.target)) this.dialog.close();
      };

      /* Built on the first quick view, when the dialog is on screen and Swiper can measure it. */
      initGallery() {
        if (this.swiper || typeof Swiper === 'undefined') return;

        const gallery = this.querySelector('[data-purchase-options-gallery]');
        if (!gallery || gallery.querySelectorAll('.swiper-slide').length < 2) return;

        this.swiper = new Swiper(gallery, {
          // Live's carousel goes round from the last image to the first
          rewind: true,
          navigation: {
            prevEl: this.querySelector('[data-purchase-options-prev]'),
            nextEl: this.querySelector('[data-purchase-options-next]'),
          },
          pagination: {
            el: this.querySelector('[data-purchase-options-dots]'),
            clickable: true,
          },
          // Swiper's a11y module sets these labels itself; its defaults are hardcoded English
          a11y: {
            prevSlideMessage: this.dataset.previousLabel,
            nextSlideMessage: this.dataset.nextLabel,
            paginationBulletMessage: (this.dataset.dotLabel || '').replace('__n__', '{{index}}'),
          },
        });
      }
    },
  );
}
